// src/data/mockNotifications.js

import { CURRENT_USER, MOCK_USERS } from "./mockUsers";

/**
 * Notification types matching NotificationsPage.js
 */
export const NOTIFICATION_TYPES = {
  COMMENT: "comment",
  LIKE: "like",
  COMMENT_LIKE: "comment_like",
  NEARBY: "nearby_note",
};

/**
 * Generate a random notification for the current user
 * Simulates different types of notifications that a user might receive
 * @param {number} id - Notification ID
 * @param {string} userId - User receiving the notification
 * @param {number} minutesAgoOffset - Offset for timestamp generation
 * @param {Array} availableNotes - Array of actual notes to reference
 */
function generateNotification(id, userId, minutesAgoOffset = 0, availableNotes = []) {
  const types = Object.values(NOTIFICATION_TYPES);
  const type = types[Math.floor(Math.random() * types.length)];

  // Random user from MOCK_USERS as actor
  const actor = MOCK_USERS[Math.floor(Math.random() * MOCK_USERS.length)];

  // Random minutes ago (between offset and offset + 2 days)
  const minutesAgo = minutesAgoOffset + Math.floor(Math.random() * (2 * 60));
  const createdAt = Date.now() - minutesAgo * 60 * 1000;

  // Pick a random note from available notes, or generate fallback data
  let noteData;
  if (availableNotes && availableNotes.length > 0) {
    const randomNote = availableNotes[Math.floor(Math.random() * availableNotes.length)];
    noteData = {
      noteId: randomNote.id,
      noteTitle: randomNote.title,
      placeName: randomNote.address?.split(',')[1]?.trim() || 'Unknown Location',
      coords: {
        lat: randomNote.latitude,
        lng: randomNote.longitude,
      },
      comments: randomNote.comments || [],
    };
  } else {
    // Fallback if no notes provided (shouldn't happen in practice)
    const noteTitles = [
      "Beautiful Sunset at the Beach",
      "Rainy Morning Thoughts",
      "Coffee Shop Vibes",
    ];
    const placeNames = ["Mission District", "Hayes Valley", "North Beach"];

    noteData = {
      noteId: `note-${Math.floor(Math.random() * 1000)}`,
      noteTitle: noteTitles[Math.floor(Math.random() * noteTitles.length)],
      placeName: placeNames[Math.floor(Math.random() * placeNames.length)],
      coords: {
        lat: 37.5 + Math.random() * 0.3,
        lng: -122.5 + Math.random() * 0.1,
      },
      comments: [],
    };
  }

  const commentSnippets = [
    "Love this thought!",
    "So beautiful!",
    "Great perspective!",
    "This is amazing!",
    "Wow, stunning!",
    "Perfect capture!",
    "This made my day!",
    "Incredible view!",
    "Love the composition!",
    "Absolutely gorgeous!",
  ];

  // Base notification object
  const notification = {
    id: `notif-${id}`,
    type,
    noteTitle: noteData.noteTitle,
    placeName: noteData.placeName,
    coords: noteData.coords,
    createdAt,
    read: Math.random() > 0.6, // 40% unread
    userId, // Owner of the notification (who receives it)
    noteId: noteData.noteId, // Referenced note - now uses actual note ID!
  };

  // Type-specific properties
  switch (type) {
    case NOTIFICATION_TYPES.COMMENT:
      // Pick random comment from the note's comments or use a snippet
      const commentForSnippet = noteData.comments.length > 0
        ? noteData.comments[Math.floor(Math.random() * noteData.comments.length)]
        : null;

      return {
        ...notification,
        actorId: actor.id,
        actorName: actor.name,
        avatarUrl: actor.avatar,
        commentSnippet: commentForSnippet?.text ||
          commentSnippets[Math.floor(Math.random() * commentSnippets.length)],
        commentId: commentForSnippet?.id, // Add comment ID for highlighting
      };

    case NOTIFICATION_TYPES.LIKE:
      // Can have multiple actors for likes
      const numActors = Math.floor(Math.random() * 5) + 1;
      const actors = [];
      for (let i = 0; i < numActors; i++) {
        const randomActor =
          MOCK_USERS[Math.floor(Math.random() * MOCK_USERS.length)];
        if (!actors.find((a) => a.id === randomActor.id)) {
          actors.push({
            id: randomActor.id,
            name: randomActor.name,
            avatarUrl: randomActor.avatar,
          });
        }
      }
      return {
        ...notification,
        actorId: actors[0].id,
        actorName: actors[0].name,
        avatarUrl: actors[0].avatar,
        actors,
      };

    case NOTIFICATION_TYPES.COMMENT_LIKE:
      // Pick random comment from the note's comments
      const likedComment = noteData.comments.length > 0
        ? noteData.comments[Math.floor(Math.random() * noteData.comments.length)]
        : null;

      return {
        ...notification,
        actorId: actor.id,
        actorName: actor.name,
        avatarUrl: actor.avatar,
        commentSnippet: likedComment?.text || "Your comment was really insightful!",
        commentId: likedComment?.id || `comment-${Math.floor(Math.random() * 1000)}`, // Use actual comment ID!
      };

    case NOTIFICATION_TYPES.NEARBY:
      return {
        ...notification,
        actorName: actor.name,
        avatarUrl: actor.avatar,
        message: `${actor.name} left a trace near you`,
      };

    default:
      return notification;
  }
}

/**
 * Generate multiple notifications for a user
 * @param {string} userId - The user who will receive these notifications
 * @param {number} count - Number of notifications to generate
 * @param {Array} availableNotes - Array of actual notes to reference
 * @returns {Array} Array of notification objects
 */
export function generateNotifications(userId, count = 20, availableNotes = []) {
  const notifications = [];
  for (let i = 0; i < count; i++) {
    notifications.push(generateNotification(i + 1, userId, i * 10, availableNotes));
  }

  // Add personalized "nearby note" notifications — one per note, with author info
  const nearbyActors = MOCK_USERS.filter((u) => u.id !== userId).slice(0, 2);
  const nearbyNotes = availableNotes.slice(0, 2);
  nearbyActors.forEach((actor, i) => {
    const note = nearbyNotes[i];
    if (!note) return;
    notifications.push({
      id: `notif-nearby-${i}`,
      type: NOTIFICATION_TYPES.NEARBY,
      noteId: note.id,
      noteTitle: note.title,
      placeName: note.address?.split(",")[1]?.trim() || null,
      coords: note.latitude
        ? { lat: note.latitude, lng: note.longitude }
        : null,
      message: `${actor.name} left a trace near you`,
      createdAt: Date.now() - (i + 1) * 10 * 60 * 1000,
      read: false,
      userId,
      actorName: actor.name,
      avatarUrl: actor.avatar,
    });
  });

  // Sort by createdAt (newest first)
  return notifications.sort((a, b) => b.createdAt - a.createdAt);
}
